import {
  Box,
  Stack,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  FormControl,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useBoolean } from 'src/hooks/use-boolean';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import { fNumber } from 'src/utils/format-number';
import { convertGold } from 'src/utils/convert';
import Iconify from 'src/components/iconify';
import { ProductType } from 'src/type/product';
import { Pagination } from 'src/type/utils';
import InfoGrid from 'src/components/ui-kit/info-grid';

// Types
type Summary = {
  totalGoldGram: number; // รวมทองคำแท่ง (หน่วยกรัม คิดจาก stock-factory.amount)
  pricePerGram: number; // ราคา/กรัม (หน่วยเงินบาท คิดจาก stock-factory.price)
  pricePerBaht: number; // ราคา/บาท (หน่วยเงินบาท คิดจาก stock-factory.price)
  totalPriceAmount: number; // จำนวนเงิน (ยอดรวม หน่วยเงินบาท ยอดรวมมูลค่าทั้งหมดที่มีในสต็อกปัจจุบัน)
  summaryGoldGram: number; // สรุปทองคำแท่ง (หน่วยกรัม)
  totalBuyPositionPendingGram: number; // ยอดจองทอง PO. (ยอด buy-position ที่ยังเป็น waiting-payment หรือ pending)
  totalSellPositionPendingGram: number; // ยอดรับซื้อ (ยอด sell-position ที่ยังเป็น waiting-ticket หรือ pending)
};

type Props = {
  onSummaryChange?: (summary: Summary) => void;
  onProductChange?: (productId: string) => void;
};

export default function ProductSummarySelector({ onSummaryChange, onProductChange }: Props) {
  const [products, setProducts] = useState<ProductType[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductType | null>(null);
  const [summary, setSummary] = useState<Summary>({
    totalGoldGram: 0,
    pricePerGram: 0,
    pricePerBaht: 0,
    totalPriceAmount: 0,
    summaryGoldGram: 0,
    totalBuyPositionPendingGram: 0,
    totalSellPositionPendingGram: 0,
  });

  const loading = useBoolean(false);
  const loadingProducts = useBoolean(false);

  // Fetch products list
  const handleGetProducts = useCallback(async () => {
    try {
      loadingProducts.onTrue();
      const { data } = await axios.get<Pagination<ProductType>>(endpoints.product.get);
      setProducts(data.results.filter((item) => !item.name.includes('รูปพรรณ')));
    } catch (error) {
      toastError(error);
    } finally {
      loadingProducts.onFalse();
    }
  }, []);

  // Fetch summary by product
  const handleGetSummary = useCallback(
    async (productId: string) => {
      try {
        loading.onTrue();
        const { data } = await axios.get<Summary>(
          endpoints.stockFactories.getSummaryByProductId(productId)
        );
        setSummary(data);
        onSummaryChange?.(data);
      } catch (error) {
        toastError(error);
      } finally {
        loading.onFalse();
      }
    },
    [onSummaryChange]
  );

  // Initialize
  useEffect(() => {
    handleGetProducts();
  }, [handleGetProducts]);

  // Handle product selection
  const handleProductChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const productId = event.target.value;
      const product = products.find((p) => p.id === productId);
      if (product) {
        setSelectedProduct(product);
        handleGetSummary(product.id);
        onProductChange?.(product.id);
      }
    },
    [products, handleGetSummary, onProductChange]
  );

  // Initialize with first product
  useEffect(() => {
    if (products.length > 0 && !selectedProduct) {
      const firstProduct = products[0];
      if (firstProduct) {
        setSelectedProduct(firstProduct);
        handleGetSummary(firstProduct.id);
        onProductChange?.(firstProduct.id);
      }
    }
  }, [products, selectedProduct, handleGetSummary, onProductChange]);

  const isOrnament = selectedProduct?.isOrnament || false;

  const summaryInfo = [
    {
      label: 'รวมทองคำแท่ง',
      value: (
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="body2" noWrap>
            {fNumber(summary.totalGoldGram || 0, 3)} กรัม
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            ≈ {fNumber(convertGold(summary.totalGoldGram || 0, 'weightBaht', isOrnament), 3)} บาท
          </Typography>
        </Stack>
      ),
      icon: <Iconify icon="mdi:package-variant" width={20} />,
    },
    {
      label: 'ราคา/กรัม',
      value: `${fNumber(summary.pricePerGram || 0, 2)} บาท`,
      icon: <Iconify icon="mdi:currency-thai-baht" width={20} />,
    },
    {
      label: 'ราคา/บาท',
      value: `${fNumber(summary.pricePerBaht || 0, 2)} บาท`,
      icon: <Iconify icon="mdi:gold" width={20} />,
    },
    {
      label: 'จำนวนเงิน',
      value: `${fNumber(summary.totalPriceAmount || 0, 2)} บาท`,
      icon: <Iconify icon="mdi:cash-multiple" width={20} />,
    },
    {
      label: 'สรุปทองคำแท่ง',
      value: (
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography
            variant={summary.summaryGoldGram < 0 ? 'subtitle2' : 'body2'}
            fontWeight={summary.summaryGoldGram < 0 ? 'bold' : 'normal'}
            color={summary.summaryGoldGram < 0 ? 'error.main' : undefined}
            noWrap
          >
            {fNumber(summary.summaryGoldGram || 0, 3)} กรัม
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            ≈ {fNumber(convertGold(summary.summaryGoldGram || 0, 'weightBaht', isOrnament), 3)} บาท
          </Typography>
        </Stack>
      ),
      icon: <Iconify icon="meteor-icons:cube" width={20} />,
      error: summary.summaryGoldGram < 0,
    },
    {
      label: 'ยอดจองทอง PO.',
      value: (
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="body2" noWrap>
            {fNumber(summary.totalBuyPositionPendingGram || 0, 3)} กรัม
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            ≈{' '}
            {fNumber(
              convertGold(summary.totalBuyPositionPendingGram || 0, 'weightBaht', isOrnament),
              3
            )}{' '}
            บาท
          </Typography>
        </Stack>
      ),
      icon: <Iconify icon="mdi:bookmark" width={20} />,
    },
    {
      label: 'ยอดรับซื้อ',
      value: (
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="body2" noWrap>
            {fNumber(summary.totalSellPositionPendingGram || 0, 3)} กรัม
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            ≈{' '}
            {fNumber(
              convertGold(summary.totalSellPositionPendingGram || 0, 'weightBaht', isOrnament),
              3
            )}{' '}
            บาท
          </Typography>
        </Stack>
      ),
      icon: <Iconify icon="mdi:trending-up" width={20} />,
    },
  ];

  return (
    <Box>
      {/* Product Selector */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">สรุปข้อมูล:</Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>เลือกสินค้า</InputLabel>
          <Select
            value={selectedProduct?.id || ''}
            label="เลือกสินค้า"
            onChange={handleProductChange}
            disabled={loadingProducts.value}
          >
            {products.map((product) => (
              <MenuItem key={product.id} value={product.id}>
                {product.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {loading.value && <CircularProgress size={16} color="inherit" />}
      </Stack>

      {/* Summary Display */}
      {selectedProduct && <InfoGrid infos={summaryInfo} />}
    </Box>
  );
}
